import React from 'react';
import { useFormik } from 'formik';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Alert, Box, Typography } from '@mui/material';
import InputField from '@components/FormControls/InputField';
import DialogFormField from '@components/FormControls/DialogFormField';
import { useMutation } from 'react-query';
import { showError, showSuccess } from '@components/notification';
import * as Yup from 'yup';
import { apiAdminChangeCustomerPassword } from '@services/customer.service';

const OAUTH_PROVIDERS = ['GOOGLE', 'APPLE', 'FACEBOOK'];

const DialogsChangePassword = ({ customer, open, setOpen }) => {
  // Backend dùng snake_case (SNAKE_CASE Jackson config) → field là user_type
  const userType = customer?.user_type;
  const isOAuthUser = userType && OAUTH_PROVIDERS.includes(userType.toUpperCase());

  const { mutateAsync, isLoading, isSuccess } = useMutation(
    ['admin-change-customer-password', customer?.id],
    (data) => apiAdminChangeCustomerPassword(customer?.id, { password: data.new_password })
  );

  const formik = useFormik({
    initialValues: {
      new_password: '',
      confirm_password: ''
    },
    validationSchema: Yup.object({
      new_password: Yup.string()
        .required('Mật khẩu mới là bắt buộc')
        .min(6, 'Mật khẩu phải có ít nhất 6 ký tự')
        .label('Mật khẩu mới'),
      confirm_password: Yup.string()
        .required('Xác nhận mật khẩu là bắt buộc')
        .oneOf([Yup.ref('new_password')], 'Mật khẩu xác nhận không khớp')
        .label('Xác nhận mật khẩu')
    }),
    onSubmit: async (values) => {
      try {
        const resp = await mutateAsync(values);
        if (resp?.data?.status?.code === 'success') {
          showSuccess({ message: 'Đổi mật khẩu thành công' });
          handleClose();
        } else {
          showError({
            message: resp?.data?.message || 'Đổi mật khẩu thất bại. Vui lòng thử lại.'
          });
        }
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message || error?.message || 'Đổi mật khẩu thất bại. Vui lòng thử lại.';
        showError({ message: errorMessage });
      }
    }
  });

  const handleClose = () => {
    setOpen(false);
    formik.resetForm();
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
      <DialogTitle>Đổi mật khẩu cho khách hàng</DialogTitle>
      <DialogContent>
        {isOAuthUser ? (
          <Box sx={{ py: 2 }}>
            <Alert severity="warning">
              Khách hàng <strong>{customer?.full_name || customer?.username}</strong> đăng nhập qua{' '}
              <strong>{userType}</strong>. Không thể đổi mật khẩu cho tài khoản này.
            </Alert>
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
              Đặt mật khẩu mới cho khách hàng{' '}
              <strong>{customer?.full_name || customer?.username}</strong>
            </Typography>
            <DialogFormField
              onCloseDialog={handleClose}
              formik={formik}
              isLoading={isLoading}
              isSuccess={isSuccess}
              actionName="Xác nhận đổi mật khẩu">
              <InputField
                formik={formik}
                name="new_password"
                required
                label="Mật khẩu mới"
                col={12}
                type="password"
              />
              <InputField
                formik={formik}
                name="confirm_password"
                required
                label="Xác nhận mật khẩu"
                col={12}
                type="password"
              />
            </DialogFormField>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DialogsChangePassword;
